import {
  Box, Button, Card, CardContent, Checkbox, Chip, Divider, FormControlLabel,
  Grid2 as Grid, IconButton, Menu, MenuItem, TextField, Typography
} from "@mui/material";
import { Add, ContentCopy, Delete, DoneAll, MoreVert } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { IUserGroup } from "../../../models/propertyGroup";
import { EWeekDay, IWorkScheduleDay, IWorkSchedule } from "../../../models/workSchedule";
import WorkScheduleService from "../../../services/workSchedule/WorkScheduleService";
import moment from "moment";
import { useTranslation } from "react-i18next";

const DEFAULT_START_TIME = "09:00:00";
const DEFAULT_END_TIME = "17:00:00";
const DEFAULT_BREAK_MINUTES = 30;

const weekdays: EWeekDay[] = [EWeekDay.Monday, EWeekDay.Tuesday, EWeekDay.Wednesday, EWeekDay.Thursday, EWeekDay.Friday, EWeekDay.Saturday, EWeekDay.Sunday];

const initDefaultDay = (weekday: EWeekDay): IWorkScheduleDay => ({
  weekday,
  isHoliday: weekday === EWeekDay.Saturday || weekday === EWeekDay.Sunday,
  startTime: (weekday === EWeekDay.Saturday || weekday === EWeekDay.Sunday) ? null : DEFAULT_START_TIME,
  endTime: (weekday === EWeekDay.Saturday || weekday === EWeekDay.Sunday) ? null : DEFAULT_END_TIME,
  breakMinutes: (weekday === EWeekDay.Saturday || weekday === EWeekDay.Sunday) ? null : DEFAULT_BREAK_MINUTES,
});

export const normalizeToPayload = (schedule: IWorkSchedule): IWorkSchedule => ({
  ...schedule,
  days: schedule.days.map(d => {
    if (d.isHoliday) return { ...d, startTime: null, endTime: null, breakMinutes: null };
    return {
      ...d,
      startTime: convertToUTCFormat(d.startTime),
      endTime: convertToUTCFormat(d.endTime),
      breakMinutes: typeof d.breakMinutes === "number" ? d.breakMinutes : DEFAULT_BREAK_MINUTES
    };
  }),
});

const convertToUTCFormat = (value?: string | null) => {
  if (!value) return null;

  const parsedTime = moment(value, ["HH:mm:ss", "HH:mm"], true);
  if (!parsedTime.isValid()) return null;

  return parsedTime.utc().format("HH:mm:ss");
};

const convertToLocalFormat = (utc?: string | null): string | null => {
  if (!utc) return null;

  const parsedTime = moment.utc(utc, ["HH:mm:ss", "HH:mm"], true);
  if (!parsedTime.isValid()) return null;

  return parsedTime.local().format("HH:mm:ss");
};

const truncateToHrsMin = (hourMinSec?: string | null) => (hourMinSec ? hourMinSec.slice(0, 5) : "");

const checkIfScheduleIsOvernight = (start?: string | null, end?: string | null) => {
  if (!start || !end) return false;
  return start > end;
};

const calculateDayMin = (day: IWorkScheduleDay): number => {
  if (day.isHoliday || !day.startTime || !day.endTime) return 0;

  const start = moment(day.startTime, ["HH:mm:ss", "HH:mm"], true);
  const end = moment(day.endTime, ["HH:mm:ss", "HH:mm"], true);
  if (!start.isValid() || !end.isValid()) return 0;

  const adaptedEnd = end.isBefore(start) ? end.clone().add(1, "day") : end;
  const totalMin = adaptedEnd.diff(start, "minutes");

  const breakMin = typeof day.breakMinutes === "number" ? day.breakMinutes : DEFAULT_BREAK_MINUTES;
  return Math.max(0, totalMin - Math.max(0, breakMin));
};

const sumScheduleMin = (schedule: IWorkSchedule): number =>
  (schedule.days ?? []).reduce((sum, day) => sum + calculateDayMin(day), 0);

const formatMinToHrsMin = (totalMin: number): string => {
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hrs}h ${mins}m`;
};

interface IWorkSchedulesSectionProps {
  userId: number;
  propertyGroups: IUserGroup[];
  propertyGroupIds?: number[];
  onSchedulesChange: (schedules: IWorkSchedule[]) => void;
  setInitialSchedules?: (schedules: IWorkSchedule[]) => void;
};

const WorkSchedulesSection = ({ userId, propertyGroups, propertyGroupIds, onSchedulesChange, setInitialSchedules }: IWorkSchedulesSectionProps) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<IWorkSchedule[]>([]);
  const [scheduleMenuAnchor, setScheduleMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedScheduleMenuId, setSelectedScheduleMenuId] = useState<number | null>(null);

  useEffect(() => {
    onSchedulesChange(schedules);
  }, [schedules, onSchedulesChange]);

  const getPropertyGroupName = (groupId: number) =>
    propertyGroups.find(pg => pg.propertyGroupId === groupId || pg.id === groupId)?.name ?? `Group #${groupId}`;

  const assignedGroups = useMemo(
    () => new Set(propertyGroupIds ?? []),
    [propertyGroups, propertyGroupIds]
  );

  useEffect(() => {
    setSchedules(prev => prev.filter(s => assignedGroups.has(s.propertyGroupId)));
  }, [assignedGroups]);

  const groupsWithSchedule = new Set(schedules.map(s => s.propertyGroupId));
  const groupsWithoutSchedule = Array.from(assignedGroups).filter(gid => !groupsWithSchedule.has(gid));

  useEffect(() => {
    if (!userId) return;

    WorkScheduleService.GetByUser(userId).then((server) => {
      const localized = server.map(s => ({
        ...s,
        days: s.days.map(d => ({
          ...d,
          startTime: convertToLocalFormat(d.startTime),
          endTime: convertToLocalFormat(d.endTime),
        })),
      }));
      setSchedules(localized);
      setInitialSchedules?.(localized);
    });
  }, [userId]);

  const onAddSchedule = (groupId: number) => {
    const newSchedule: IWorkSchedule = {
      userId,
      propertyGroupId: groupId,
      days: weekdays.map(initDefaultDay),
    };
    setSchedules(prev => [...prev, newSchedule]);
  };

  const onRemoveSchedule = async (schedule: IWorkSchedule) => {
    setSchedules(prev => prev.filter(s => s !== schedule));
  };

  const setDefaultWorkSchedule = (schedule: IWorkSchedule) => {
    const days = weekdays.map(initDefaultDay);
    updateSchedule(schedule, days);
  };

  const copyMondayToAll = (schedule: IWorkSchedule) => {
    const monday = schedule.days.find(d => d.weekday === EWeekDay.Monday);
    if (!monday) return;

    const mondayScheduleData = { ...monday };
    const days = schedule.days.map(d => {
      return {
        ...d,
        isHoliday: mondayScheduleData.isHoliday,
        startTime: mondayScheduleData.startTime,
        endTime: mondayScheduleData.endTime,
        breakMinutes: mondayScheduleData.breakMinutes,
      };
    });

    updateSchedule(schedule, days);
  };

  const updateSchedule = (schedule: IWorkSchedule, days: IWorkScheduleDay[]) => {
    setSchedules(prev => prev.map(s => (s === schedule ? { ...s, days } : s)));
  };

  const updateDay = (schedule: IWorkSchedule, idx: number, patch: Partial<IWorkScheduleDay>) => {
    const days = schedule.days.slice();
    days[idx] = { ...days[idx], ...patch };

    if (patch.isHoliday) {
      days[idx] = { ...days[idx], startTime: null, endTime: null, breakMinutes: null };
    }

    setSchedules(prev => prev.map(s => (s === schedule ? { ...s, days } : s)));
  };

  const minPerSchedule = useMemo(
    () =>
      new Map<number, number>(
        schedules.map((s) => [s.propertyGroupId, sumScheduleMin(s)])
      ),
    [schedules]
  );

  const overallMin = useMemo(
    () => schedules.reduce((sum, s) => sum + sumScheduleMin(s), 0),
    [schedules]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontWeight={700}>{t("schedule.title")}</Typography>
          <Chip
            size="small"
            variant="outlined"
            label={`${t("schedule.badges.overallTotal")}: ${formatMinToHrsMin(overallMin)}`}
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            disabled={groupsWithoutSchedule.length === 0}
            onClick={(e) => {
              setScheduleMenuAnchor(e.currentTarget);
              setSelectedScheduleMenuId(-1);
            }}
          >
            {t("schedule.add")}
          </Button>
          <Menu
            anchorEl={scheduleMenuAnchor}
            open={Boolean(scheduleMenuAnchor) && selectedScheduleMenuId === -1}
            onClose={() => { setScheduleMenuAnchor(null); setSelectedScheduleMenuId(null); }}
          >
            {groupsWithoutSchedule.map(gid => (
              <MenuItem key={gid} onClick={() => { onAddSchedule(gid); setScheduleMenuAnchor(null); }}>
                {getPropertyGroupName(gid)}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {schedules.length === 0 && (
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary">{t("schedule.noSchedules")}</Typography>
          </Grid>
        )}
        {schedules.map((schedule) => (
          <Grid key={`${schedule.propertyGroupId}-${schedule.id ?? "new"}`} size={{ xs: 12, lg: 6, xl: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography fontWeight={700}>  {t("schedule.cardTitle", { group: getPropertyGroupName(schedule.propertyGroupId) })}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${t("schedule.badges.total")}: ${formatMinToHrsMin(minPerSchedule.get(schedule.propertyGroupId) ?? 0)}`}
                    />
                    <IconButton
                      onClick={(e) => { setScheduleMenuAnchor(e.currentTarget); setSelectedScheduleMenuId(schedule.propertyGroupId ?? 0); }}
                      aria-label="schedule actions"
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Menu
                    anchorEl={scheduleMenuAnchor}
                    open={Boolean(scheduleMenuAnchor) && selectedScheduleMenuId === (schedule.propertyGroupId ?? 0)}
                    onClose={() => { setScheduleMenuAnchor(null); setSelectedScheduleMenuId(null); }}
                  >
                    <MenuItem onClick={() => { setDefaultWorkSchedule(schedule); setScheduleMenuAnchor(null); }}>
                      <DoneAll sx={{ mr: 1 }} /> {t("schedule.actions.applyDefault")}
                    </MenuItem>
                    <MenuItem onClick={() => { copyMondayToAll(schedule); setScheduleMenuAnchor(null); }}>
                      <ContentCopy sx={{ mr: 1 }} /> {t("schedule.actions.copyMonday")}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { onRemoveSchedule(schedule); setScheduleMenuAnchor(null); }}>
                      <Delete color="error" sx={{ mr: 1 }} /> {t("schedule.actions.remove")}
                    </MenuItem>
                  </Menu>


                </Box>

                <Divider sx={{ mb: 2 }} />

                {schedule.days.sort((a, b) => weekdays.indexOf(a.weekday) - weekdays.indexOf(b.weekday)).map((day, idx) => {
                  const isScheduleOvernight = checkIfScheduleIsOvernight(day.startTime, day.endTime);
                  return (
                    <Box key={day.weekday} sx={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr 1fr", gap: 1, alignItems: "center", mb: 1 }}>
                      <Typography sx={{ fontWeight: 500, mr: 1, minWidth: 90 }}>{t(`schedule.${EWeekDay[day.weekday].toLowerCase()}`)}</Typography>

                      <TextField
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={day.isHoliday}
                        value={truncateToHrsMin(day.startTime)}
                        onChange={e => updateDay(schedule, idx, {
                          startTime: e.target.value ? `${e.target.value}:00` : null
                        })}
                        label={t("schedule.fields.start")}
                      />

                      <TextField
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={day.isHoliday}
                        value={truncateToHrsMin(day.endTime)}
                        onChange={e => updateDay(schedule, idx, {
                          endTime: e.target.value ? `${e.target.value}:00` : null
                        })}
                        label={t("schedule.fields.end")}
                      />

                      <TextField
                        type="number"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        placeholder="--"
                        disabled={day.isHoliday}
                        value={day.breakMinutes ?? ""}
                        onChange={e => updateDay(schedule, idx, { breakMinutes: e.target.value === "" ? null : Math.max(0, Math.min(Number(e.target.value), 120)) })}
                        label={t("schedule.fields.break")}
                      />

                      <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={day.isHoliday}
                              size="small"
                              onChange={(e) => updateDay(schedule, idx, { isHoliday: e.target.checked })}
                            />
                          }
                          label={t("schedule.fields.holiday")}
                        />
                        {!day.isHoliday && isScheduleOvernight && (
                          <Chip size="small" label={t("schedule.badges.isOvernight")} sx={{ ml: 1 }} color="primary" variant="outlined" />
                        )}
                      </Box>

                      <Divider sx={{ gridColumn: "1 / -1", mb: 1 }} />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WorkSchedulesSection;
